import React from 'react';

import {
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';

import {ProcedureChangeParams} from './EditProcedureModal';
import SelectedProcedureEditor from './SelectedProcedureEditor';
import {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import {SettingsTabContents} from './StudyDesignTabSharedComponents';
import {Condition, ConditionGroup} from './useExperimentDesignState';
import {MethodExecutionLimitState} from './useMethodExecutionLimitState';

interface BaseProcedureRunDesignEditorLeftSidepanelProps<TCondition> {
  isPlan: boolean;
  conditions: Array<TCondition>;
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void;
  selectedMethodId: string;
  experimentalInputs: ExperimentalInputs;
  setExperimentalInputs: (experimentalInputs: ExperimentalInputs) => void;
  editState: StudyDesignTabEditState;
  procedureType: ProcedureType;
  methodExecutionLimitState: MethodExecutionLimitState;
  studyType: StudyType;
  dataEntryMode: ProcedureRunDataEntryMode;
  procedureId: string;
  flowchartVersionId: string;
}

interface ProcedureRunDesignEditorLeftSidepanelPlanProps extends BaseProcedureRunDesignEditorLeftSidepanelProps<ConditionGroup> {
  isPlan: true;
  onProcedureChange: (params: ProcedureChangeParams) => Promise<void>;
  setDataEntryMode: (dataEntryMode: ProcedureRunDataEntryMode) => void;
}

interface ProcedureRunDesignEditorLeftSidepanelRunProps extends BaseProcedureRunDesignEditorLeftSidepanelProps<Condition> {
  isPlan: false;
}

type ProcedureRunDesignEditorLeftSidepanelProps =
  | ProcedureRunDesignEditorLeftSidepanelPlanProps
  | ProcedureRunDesignEditorLeftSidepanelRunProps;

export default function ProcedureRunDesignEditorLeftSidepanel(
  props: ProcedureRunDesignEditorLeftSidepanelProps
): JSX.Element {
  const onProcedureChange = props.isPlan ? props.onProcedureChange : async () => {};
  const isLocked = props.editState !== StudyDesignTabEditState.EDITING;
  const isEditable = props.isPlan && !isLocked;
  const settingsTabContents = props.isPlan ? (
    <SettingsTabContents
      conditions={props.conditions}
      setConditions={props.setConditions}
      selectedMethodId={props.selectedMethodId}
      experimentalInputs={props.experimentalInputs}
      setExperimentalInputs={props.setExperimentalInputs}
      locked={isLocked}
      procedureType={props.procedureType}
      methodExecutionLimitState={props.methodExecutionLimitState}
      studyType={props.studyType}
      dataEntryMode={props.dataEntryMode}
      // The new component isn't going to have an estimate result, so just pass a placeholder for now
      estimateResult={{stale: false, executionPlan: null}}
      setDataEntryMode={props.setDataEntryMode}
      isPlan={true}
    />
  ) : (
    <SettingsTabContents
      conditions={props.conditions}
      setConditions={props.setConditions}
      selectedMethodId={props.selectedMethodId}
      experimentalInputs={props.experimentalInputs}
      setExperimentalInputs={props.setExperimentalInputs}
      locked={isLocked}
      procedureType={props.procedureType}
      methodExecutionLimitState={props.methodExecutionLimitState}
      studyType={props.studyType}
      dataEntryMode={props.dataEntryMode}
      // The new component isn't going to have an estimate result, so just pass a placeholder for now
      estimateResult={{stale: false, executionPlan: null}}
      isPlan={false}
    />
  );

  return (
    <>
      {/* TODO (BNCH-153425): Replace this with an updated component */}
      {settingsTabContents}
      {/* TODO (BNCH-156168): Replace this with a new component */}
      <SelectedProcedureEditor
        isEditable={isEditable}
        procedureId={props.procedureId}
        flowchartVersionId={props.flowchartVersionId}
        procedureType={props.procedureType}
        studyType={props.studyType}
        onProcedureChange={onProcedureChange}
      />
    </>
  );
}
